'use client'
import { FC } from 'react'
import {
  SUMMER_SALE_DEADLINE_TIME,
  ANNUAL_PLAN_SALE_DEADLINE_TIME,
} from '@/constant/date'
import LifecycleLink from '@/components/common/LifecycleLink'
import { BannerGemsSvg } from 'Comps/svg/BannerGemsSvg'
import { DoubleArrowRightMore } from 'Icons'
import { URL_SUBSCRIBE } from '@/constant/subscribe'
import useCountdown from 'Hooks/useCountdown'
import { useRecoilValue } from 'recoil'
import { userState } from '@/store/user'
import useI18n from 'Hooks/useI18n'
import { withLocale } from '@/locales'

interface BannerProps {
  background: string
  text: string
  dd: number
  hh: number
  mm: number
  ss: number
  onClick?: () => void
  href?: string
  showRightIcon?: boolean
  idPrefix: string
  showCountdown?: boolean
}

const Banner: FC<BannerProps> = ({
  background,
  text,
  dd,
  hh,
  mm,
  ss,
  onClick,
  href,
  showRightIcon = false,
  idPrefix,
  showCountdown = true,
}) => {
  const CountdownDisplay = () => (
    <div className="ml-1 mr-1 flex items-center font-mono text-xs">
      <span className="flex h-5 w-6 items-center justify-center rounded-md bg-[#ff8a8a]/10 text-center text-[10px]">
        {dd}d
      </span>
      <span className="px-0.5">:</span>
      <span className="flex h-5 w-6 items-center justify-center rounded-md bg-[#ff8a8a]/10 text-center text-[10px]">
        {hh}h
      </span>
      <span className="px-0.5">:</span>
      <span className="flex h-5 w-6 items-center justify-center rounded-md bg-[#ff8a8a]/10 text-center text-[10px]">
        {mm}m
      </span>
      <span className="px-0.5">:</span>
      <span className="flex h-5 w-6 items-center justify-center rounded-md bg-[#ff8a8a]/10 text-center text-[10px]">
        {ss}s
      </span>
    </div>
  )

  const content = (
    <>
      <BannerGemsSvg
        idPrefix={idPrefix}
        height="36"
        className="ml-3 min-w-[40.19px] translate-y-0.5 scale-125"
      />
      <p className="whitespace-nowrap text-[12px] tracking-wide">{text}</p>
      {showCountdown && <CountdownDisplay />}
      {showRightIcon && (
        <DoubleArrowRightMore style={{ height: '30px', minWidth: '30px' }} />
      )}
    </>
  )

  const className =
    'no-scrollbar pointer-events-auto mx-1.5 mb-2 mt-2 flex h-9 min-w-0 cursor-pointer items-center justify-center overflow-x-auto overflow-y-hidden rounded-full border border-white/20'

  if (href) {
    return (
      <LifecycleLink href={href} className={className} style={{ background }}>
        {content}
      </LifecycleLink>
    )
  }

  return (
    <div onClick={onClick} className={className} style={{ background }}>
      {content}
    </div>
  )
}

const AnnualPlanBanner: FC = () => {
  const user = useRecoilValue(userState)
  const { t, lang } = useI18n()

  const { isExpired, dd, hh, mm, ss } = useCountdown(
    user?.user_id ? Number(user?.user_id) / 1000000 + 3 * 86400000 : 0
  )
  const {
    isExpired: isExpiredSummerSale,
    dd: ddSummerSale,
    hh: hhSummerSale,
    mm: mmSummerSale,
    ss: ssSummerSale,
  } = useCountdown(SUMMER_SALE_DEADLINE_TIME)

  const {
    isExpired: isExpiredAnnualPlanSale,
    dd: ddAnnualPlanSale,
    hh: hhAnnualPlanSale,
    mm: mmAnnualPlanSale,
    ss: ssAnnualPlanSale,
  } = useCountdown(ANNUAL_PLAN_SALE_DEADLINE_TIME)

  return (
    <>
      {(!isExpired || !isExpiredAnnualPlanSale || !isExpiredSummerSale) && (
        <Banner
          background="linear-gradient(255deg, rgba(132,81,116,0.7) 0%, rgba(113,80,75,0.85) 58%, rgba(94,101,79,0.75) 98%)"
          text={
            !isExpiredAnnualPlanSale
              ? t('annualPlanCountdownText')
              : !isExpired
                ? t('New-User Subscription Deals!')
                : t('Summer Sale!')
          }
          dd={Number(
            !isExpiredAnnualPlanSale
              ? ddAnnualPlanSale
              : !isExpired
                ? dd
                : ddSummerSale
          )}
          hh={Number(
            !isExpiredAnnualPlanSale
              ? hhAnnualPlanSale
              : !isExpired
                ? hh
                : hhSummerSale
          )}
          mm={Number(
            !isExpiredAnnualPlanSale
              ? mmAnnualPlanSale
              : !isExpired
                ? mm
                : mmSummerSale
          )}
          ss={Number(
            !isExpiredAnnualPlanSale
              ? ssAnnualPlanSale
              : !isExpired
                ? ss
                : ssSummerSale
          )}
          href={withLocale(URL_SUBSCRIBE, lang)}
          showRightIcon={true}
          idPrefix="home-desktop-banner"
        />
      )}
    </>
  )
}

export default AnnualPlanBanner
